import { useEffect, useRef, useState } from 'react';
import type { GifResult, MessageAttachment } from '@itsappy/shared';
import { useGifSearch, useStickerPacks } from '@/hooks/queries';
import { Spinner } from '@/components/ui';
import { cn } from '@/lib/utils';

export interface GifPickerProps {
  /** Called with an attachment ready to send on its own. */
  onPick: (attachment: MessageAttachment) => void;
  onClose: () => void;
  className?: string;
}

/**
 * GIFs and stickers behind one button.
 *
 * A GIF is posted by reference — the attachment points at Giphy's CDN rather
 * than copying the file into workspace storage, which keeps a throwaway
 * reaction out of the file library and off the storage bill. Stickers are the
 * opposite: they are the org's own uploads, so they already live in storage.
 */
export function GifPicker({ onPick, onClose, className }: GifPickerProps) {
  const [tab, setTab] = useState<'gifs' | 'stickers'>('gifs');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const gifs = useGifSearch(debounced, tab === 'gifs');
  const { data: packs, isLoading: packsLoading } = useStickerPacks();

  useEffect(() => {
    // One request per pause in typing, not one per keystroke.
    const timer = window.setTimeout(() => setDebounced(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) onClose();
    };
    window.addEventListener('keydown', onKey);
    const timer = window.setTimeout(
      () => window.addEventListener('mousedown', onPointerDown),
      0,
    );
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onPointerDown);
    };
  }, [onClose]);

  function pickGif(gif: GifResult) {
    onPick({
      // Giphy's id, not an ObjectId: a GIF is never a FileAsset of ours.
      id: `gif-${gif.id}`,
      name: gif.title,
      url: gif.url,
      mimeType: 'image/gif',
      size: gif.size,
      kind: 'gif',
    });
  }

  const stickerPacks = packs?.items ?? [];
  const gifError = gifs.error;

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label="Choose a GIF or sticker"
      className={cn(
        'flex w-[min(23rem,calc(100vw-2rem))] flex-col rounded-xl bg-surface-2 shadow-overlay ring-1 ring-surface-3',
        className,
      )}
    >
      <div className="flex gap-1 border-b border-surface-3 p-2">
        {(['gifs', 'stickers'] as const).map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            aria-pressed={tab === id}
            className={cn(
              'rounded-lg px-2.5 py-1 text-xs font-medium capitalize transition-colors',
              tab === id ? 'bg-surface-3 text-ink' : 'text-slate-400 hover:bg-surface-3',
            )}
          >
            {id}
          </button>
        ))}
        {tab === 'gifs' && (
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search GIFs"
            aria-label="Search GIFs"
            className="min-w-0 flex-1 rounded-lg bg-surface-3 px-2.5 py-1 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:ring-1 focus:ring-brand-500"
          />
        )}
      </div>

      <div className="max-h-72 min-h-40 overflow-y-auto p-2">
        {tab === 'gifs' ? (
          gifs.isLoading ? (
            <div className="grid h-32 place-items-center">
              <Spinner />
            </div>
          ) : gifError ? (
            <p className="px-2 py-6 text-center text-xs text-slate-500">
              {gifError instanceof Error ? gifError.message : 'GIF search is unavailable.'}
            </p>
          ) : (gifs.data?.items.length ?? 0) === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-slate-500">
              {debounced ? `Nothing for “${debounced}”.` : 'No GIFs to show.'}
            </p>
          ) : (
            /* Two masonry-ish columns: GIFs are wildly different heights and a
               fixed grid would letterbox most of them. */
            <div className="columns-2 gap-2 [&>*]:mb-2">
              {gifs.data?.items.map((gif) => (
                <button
                  key={gif.id}
                  type="button"
                  onClick={() => pickGif(gif)}
                  className="block w-full overflow-hidden rounded-lg ring-surface-4 transition hover:ring-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <img
                    src={gif.previewUrl}
                    alt={gif.title}
                    loading="lazy"
                    className="w-full bg-surface-3 object-cover"
                  />
                </button>
              ))}
            </div>
          )
        ) : packsLoading ? (
          <div className="grid h-32 place-items-center">
            <Spinner />
          </div>
        ) : stickerPacks.length === 0 ? (
          <p className="px-2 py-6 text-center text-xs text-slate-500">
            No sticker packs yet. You can add one in Settings.
          </p>
        ) : (
          stickerPacks.map((pack) => (
            <div key={pack.id} className="mb-3">
              <p className="px-1 pb-1 text-[11px] uppercase tracking-wide text-slate-500">
                {pack.name}
              </p>
              <div className="grid grid-cols-4 gap-1">
                {pack.stickers.map((sticker) => (
                  <button
                    key={sticker.id}
                    type="button"
                    title={sticker.name}
                    onClick={() =>
                      onPick({
                        id: sticker.id,
                        name: sticker.name,
                        url: sticker.url,
                        mimeType: 'image/png',
                        size: 0,
                        kind: 'sticker',
                      })
                    }
                    className="grid aspect-square place-items-center rounded-lg p-1 transition-colors hover:bg-surface-3"
                  >
                    <img
                      src={sticker.url}
                      alt={sticker.name}
                      loading="lazy"
                      className="max-h-full max-w-full object-contain"
                    />
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {tab === 'gifs' && (
        <p className="border-t border-surface-3 px-2.5 py-1.5 text-[10px] text-slate-500">
          GIFs by GIPHY
        </p>
      )}
    </div>
  );
}
